import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import GameInfoDialog from './GameInfoDialog';
import GameStats from './GameStats';
import { richTextToPlain } from '../utils/richText';
import { formatMinBet, formatRtp } from '../utils/format';

export { formatMinBet, formatRtp };

// ─── SVG Thumbnails ───────────────────────────────────────────────────────────
function RabbitRushThumb() {
  return (
    <svg viewBox="0 0 220 140" width="100%" height="100%" className="game-thumb-svg">
      <rect width="220" height="140" fill="#0d0122" rx="12" />
      {[[20,15],[60,8],[110,20],[160,10],[195,25],[40,35],[140,30],[80,45],[180,40]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r="1.2" fill="#fff" opacity={0.5+i%3*0.2} />
      ))}
      {[30,72,114,156].map((x, i) => (
        <g key={i}>
          <rect x={x} y={90} width={38} height={30} rx="6"
            fill={i < 2 ? '#1a5c38' : '#0d3320'}
            stroke={i < 2 ? '#7c3aed' : 'rgba(124,58,237,0.3)'}
            strokeWidth="1.2" />
          <text x={x+19} y={110} textAnchor="middle" fontSize="9" fill={i<2?'#7c3aed':'#4a7a6a'} fontWeight="bold">
            {['1.2×','1.8×','2.5×','4.0×'][i]}
          </text>
        </g>
      ))}
      <line x1="68" y1="105" x2="72" y2="105" stroke="#7c3aed" strokeWidth="1" strokeDasharray="3,2" opacity="0.5" />
      <line x1="110" y1="105" x2="114" y2="105" stroke="#7c3aed" strokeWidth="1" strokeDasharray="3,2" opacity="0.5" />
      <line x1="152" y1="105" x2="156" y2="105" stroke="rgba(124,58,237,0.3)" strokeWidth="1" strokeDasharray="3,2" opacity="0.4" />
      <g transform="translate(80, 55)">
        <ellipse cx="0" cy="18" rx="8" ry="7" fill="#f0e0ff" />
        <circle cx="0" cy="9" r="6.5" fill="#f0e0ff" />
        <ellipse cx="-3" cy="-1" rx="2" ry="6" fill="#f0e0ff" />
        <ellipse cx="-3" cy="-1" rx="1.2" ry="4.5" fill="#ffc0cb" />
        <ellipse cx="3" cy="-1" rx="2" ry="6" fill="#f0e0ff" />
        <ellipse cx="3" cy="-1" rx="1.2" ry="4.5" fill="#ffc0cb" />
        <circle cx="-2.5" cy="8" r="1.2" fill="#1a0533" />
        <circle cx="2.5" cy="8" r="1.2" fill="#1a0533" />
        <ellipse cx="0" cy="25" rx="8" ry="2" fill="#7c3aed" opacity="0.2" />
      </g>
      {[[-8,-12],[10,-18],[20,-8],[14,-22],[-4,-20]].map(([dx,dy],i)=>(
        <circle key={i} cx={80+dx} cy={55+dy} r="3" fill="#f5c842" opacity={0.7-i*0.1} />
      ))}
      <text x="110" y="135" textAnchor="middle" fontSize="10" fill="#94a3b8" letterSpacing="1">
        RABBIT RUSH
      </text>
    </svg>
  );
}

function DefaultThumb({ name }) {
  return (
    <svg viewBox="0 0 220 140" width="100%" height="100%" className="game-thumb-svg">
      <rect width="220" height="140" fill="#0d0122" rx="12" />
      <text x="110" y="75" textAnchor="middle" fontSize="36" fill="#a78bfa" opacity="0.5">🎮</text>
      <text x="110" y="115" textAnchor="middle" fontSize="11" fill="#94a3b8">{name.toUpperCase()}</text>
    </svg>
  );
}

const THUMBNAILS = {
  'rabbit-rush': RabbitRushThumb,
};

function GameThumb({ slug, name, thumbnailUrl }) {
  if (thumbnailUrl) return <img src={thumbnailUrl} alt={name} className="game-thumb-img" />;
  const Thumb = THUMBNAILS[slug];
  if (Thumb) return <Thumb name={name} />;
  return <DefaultThumb name={name} />;
}

// ─── Game Card ────────────────────────────────────────────────────────────────
export default function GameCard({ game }) {
  const slug = game.slug || game.id;
  const [infoOpen, setInfoOpen] = useState(false);
  // the card shows a clean 2-line text preview; the formatted description
  // (admin RichEditor HTML) opens in GameInfoDialog
  const excerpt = useMemo(() => richTextToPlain(game.description), [game.description]);
  return (
    <div className="game-card">
      <Link to={`/game/play/${slug}`} className="game-card-thumb">
        <GameThumb slug={slug} name={game.name} thumbnailUrl={game.thumbnail_url} />
        <div className="game-card-thumb-scrim" />
        <div className="game-card-tags">
          <span className="tag tag-hot">HOT</span>
          <span className="tag tag-new">NEW</span>
        </div>
      </Link>
      <div className="game-card-body">
        <div className="game-card-heading">
          <div className="game-card-category">{game.category}</div>
          <h3 className="game-card-name">{game.name}</h3>
        </div>
        {excerpt && (
          <div className="game-card-about">
            <p className="game-card-desc">{excerpt}</p>
            <button type="button" className="game-card-more" onClick={() => setInfoOpen(true)} aria-haspopup="dialog">
              Game details
            </button>
          </div>
        )}
        <GameStats game={game} />
        <Link className="btn-demo" to={`/game/play/${slug}`}>
          Play Demo <span className="btn-demo-arrow">→</span>
        </Link>
      </div>
      {excerpt && <GameInfoDialog game={game} open={infoOpen} onClose={() => setInfoOpen(false)} />}
    </div>
  );
}

export { GameThumb };
